/* istanbul ignore file */
/* tslint:disable */
/* eslint-disable */
import type { AuthenticationUrl } from '../models/AuthenticationUrl';
import type { Body_azure_sso_callback } from '../models/Body_azure_sso_callback';
import type { CheckMfaCodePayload } from '../models/CheckMfaCodePayload';
import type { MfaChallengePayload } from '../models/MfaChallengePayload';
import type { MfaToken } from '../models/MfaToken';
import type { PasswordCreatePayload } from '../models/PasswordCreatePayload';
import type { PasswordLoginPayload } from '../models/PasswordLoginPayload';
import type { RefreshTokenPayload } from '../models/RefreshTokenPayload';
import type { SMSCodePayload } from '../models/SMSCodePayload';
import type { SMSEnablePayload } from '../models/SMSEnablePayload';
import type { SMSSetupPayload } from '../models/SMSSetupPayload';
import type { SMSSetupResponse } from '../models/SMSSetupResponse';
import type { Token } from '../models/Token';
import type { TokenContent } from '../models/TokenContent';
import type { TOTPEnablePayload } from '../models/TOTPEnablePayload';
import type { TOTPSetupPayload } from '../models/TOTPSetupPayload';
import type { TOTPSetupResponse } from '../models/TOTPSetupResponse';
import type { UserCreate } from '../models/UserCreate';

import type { CancelablePromise } from '../core/CancelablePromise';
import type { BaseHttpRequest } from '../core/BaseHttpRequest';

export class AuthService {

    constructor(public readonly httpRequest: BaseHttpRequest) {}

    /**
     * Generate Email Challenge
     * Send user a login magic link email
     * @param requestBody
     * @returns any Successful Response
     * @throws ApiError
     */
    public generateEmailChallenge(
        requestBody: UserCreate,
    ): CancelablePromise<any> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/auth/generate-email-challenge',
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Generate Setup Email
     * Send user a initial password setup email
     * @param requestBody
     * @returns any Successful Response
     * @throws ApiError
     */
    public generateSetupEmail(
        requestBody: UserCreate,
    ): CancelablePromise<any> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/auth/generate-setup-email',
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Generate Password Reset Email
     * Send user a password reset email
     * @param requestBody
     * @returns any Successful Response
     * @throws ApiError
     */
    public generatePasswordResetEmail(
        requestBody: UserCreate,
    ): CancelablePromise<any> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/auth/generate-password-reset-email',
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Authenticate Email Challenge
     * Authenticate email challenge
     * @param userId
     * @param token
     * @returns any Successful Response
     * @throws ApiError
     */
    public authenticateEmailChallenge(
        userId: string,
        token: string,
    ): CancelablePromise<(Token | MfaToken)> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/auth/authenticate-email-challenge/{user_id}/{token}',
            path: {
                'user_id': userId,
                'token': token,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Authenticate Password
     * If a user has MFA enabled, this will return a MFA Token which
     * is part of the requirements for authenticating in authenticate_mfa
     * Otherwise, it will return a full Access / Refresh Token
     * @param requestBody
     * @returns any Successful Response
     * @throws ApiError
     */
    public authenticatePassword(
        requestBody: PasswordLoginPayload,
    ): CancelablePromise<(Token | MfaToken)> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/auth/authenticate-password',
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Get Azure Sso Login Url
     * Get the Azure SSO login URL
     * @returns string Successful Response
     * @throws ApiError
     */
    public getAzureSsoLoginUrl(): CancelablePromise<string> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/auth/azure-sso-login-url',
        });
    }

    /**
     * Azure Sso Callback
     * @param requestBody
     * @returns any Successful Response
     * @throws ApiError
     */
    public azureSsoCallback(
        requestBody: Body_azure_sso_callback,
    ): CancelablePromise<any> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/auth/azure-sso-callback',
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Initiate Oidc Login
     * Start OIDC flow - redirect to IDP using internal provider ID
     * @param providerId
     * @returns AuthenticationUrl Successful Response
     * @throws ApiError
     */
    public initiateOidcLogin(
        providerId: string,
    ): CancelablePromise<AuthenticationUrl> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/auth/oidc/{provider_id}/login',
            path: {
                'provider_id': providerId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Initiate Oidc Login By Client Id
     * Start OIDC flow using OAuth client ID - for IdP-initiated SSO flows
     *
     * This endpoint is used when users come from IdP portals (Azure AD My Apps, Okta, etc.)
     * where we know the client_id but not the internal provider ID.
     *
     * The client_id must match the OAuth client ID configured in both:
     * - The IdP (Azure AD, Okta, etc.)
     * - The OIDC provider settings in our database
     * @param clientId
     * @returns AuthenticationUrl Successful Response
     * @throws ApiError
     */
    public initiateOidcLoginByClientId(
        clientId: string,
    ): CancelablePromise<AuthenticationUrl> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/auth/oidc/initiate/{client_id}/login',
            path: {
                'client_id': clientId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Oidc Callback
     * Handle IDP callback
     * @param code
     * @param state
     * @returns Token Successful Response
     * @throws ApiError
     */
    public oidcCallback(
        code: string,
        state: string,
    ): CancelablePromise<Token> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/auth/oidc/callback',
            query: {
                'code': code,
                'state': state,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Refresh Jwt Token
     * @param requestBody
     * @returns Token Successful Response
     * @throws ApiError
     */
    public refreshJwtToken(
        requestBody: RefreshTokenPayload,
    ): CancelablePromise<Token> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/auth/refresh',
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Introspect Token
     * Get OAuth2 access token
     * @returns TokenContent Successful Response
     * @throws ApiError
     */
    public introspectToken(): CancelablePromise<TokenContent> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/auth/introspect',
        });
    }

    /**
     * Update Password
     * @param userId
     * @param requestBody
     * @returns any Successful Response
     * @throws ApiError
     */
    public updatePassword(
        userId: string,
        requestBody: PasswordCreatePayload,
    ): CancelablePromise<any> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/auth/update-password',
            query: {
                'user_id': userId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Generate Mfa Challenge
     * @param requestBody
     * @returns any Successful Response
     * @throws ApiError
     */
    public generateMfaChallenge(
        requestBody: MfaChallengePayload,
    ): CancelablePromise<any> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/auth/generate-mfa-challenge',
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Authenticate Mfa
     * @param requestBody
     * @returns Token Successful Response
     * @throws ApiError
     */
    public authenticateMfa(
        requestBody: CheckMfaCodePayload,
    ): CancelablePromise<Token> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/auth/authenticate-mfa',
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Get Staff Oidc Provider
     * Get the staff OIDC provider configuration if available.
     * Returns None if not configured in environment.
     * @returns any Successful Response
     * @throws ApiError
     */
    public getStaffOidcProvider(): CancelablePromise<(Record<string, any> | null)> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/auth/get-staff-oidc-provider',
        });
    }

    /**
     * Get Cluster Auth Settings
     * Get cluster authentication settings via the cluster's entity (requires cluster admin permission)
     * @param clusterId
     * @returns any Successful Response
     * @throws ApiError
     */
    public getClusterAuthSettings(
        clusterId: string,
    ): CancelablePromise<Record<string, any>> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/auth/cluster/{cluster_id}/auth-settings',
            path: {
                'cluster_id': clusterId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Update Cluster Auth Settings
     * Update cluster authentication settings via the cluster's entity (requires cluster admin permission)
     * @param clusterId
     * @param requestBody
     * @returns any Successful Response
     * @throws ApiError
     */
    public updateClusterAuthSettings(
        clusterId: string,
        requestBody: Record<string, any>,
    ): CancelablePromise<Record<string, any>> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/auth/cluster/{cluster_id}/auth-settings',
            path: {
                'cluster_id': clusterId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Get Cluster Oidc Provider
     * Get the cluster's OIDC provider configuration via the cluster's entity (requires cluster admin permission)
     * @param clusterId
     * @returns any Successful Response
     * @throws ApiError
     */
    public getClusterOidcProvider(
        clusterId: string,
    ): CancelablePromise<(Record<string, any> | null)> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/auth/get-cluster-oidc-provider',
            query: {
                'cluster_id': clusterId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Save Cluster Oidc Provider
     * Create or update cluster OIDC provider configuration via the cluster's entity (requires cluster admin permission)
     * @param clusterId
     * @param requestBody
     * @returns any Successful Response
     * @throws ApiError
     */
    public saveClusterOidcProvider(
        clusterId: string,
        requestBody: Record<string, any>,
    ): CancelablePromise<Record<string, any>> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/auth/save-cluster-oidc-provider',
            query: {
                'cluster_id': clusterId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Test Cluster Oidc Provider
     * Test cluster OIDC provider configuration (requires cluster admin permission)
     * @param clusterId
     * @returns any Successful Response
     * @throws ApiError
     */
    public testClusterOidcProvider(
        clusterId: string,
    ): CancelablePromise<Record<string, any>> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/auth/test-cluster-oidc-provider',
            query: {
                'cluster_id': clusterId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Get Entity Auth Settings
     * Get entity authentication settings (requires entity admin permission)
     * @param entityId
     * @returns any Successful Response
     * @throws ApiError
     */
    public getEntityAuthSettings(
        entityId: string,
    ): CancelablePromise<Record<string, any>> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/auth/entity/{entity_id}/auth-settings',
            path: {
                'entity_id': entityId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Update Entity Auth Settings
     * Update entity authentication settings (requires entity admin permission)
     * @param entityId
     * @param requestBody
     * @returns any Successful Response
     * @throws ApiError
     */
    public updateEntityAuthSettings(
        entityId: string,
        requestBody: Record<string, any>,
    ): CancelablePromise<Record<string, any>> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/auth/entity/{entity_id}/auth-settings',
            path: {
                'entity_id': entityId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Get Entity Oidc Provider
     * Get the entity's OIDC provider configuration (requires entity admin permission)
     * @param entityId
     * @returns any Successful Response
     * @throws ApiError
     */
    public getEntityOidcProvider(
        entityId: string,
    ): CancelablePromise<(Record<string, any> | null)> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/auth/get-entity-oidc-provider',
            query: {
                'entity_id': entityId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Save Entity Oidc Provider
     * Create or update entity OIDC provider configuration (requires entity admin permission)
     * @param entityId
     * @param requestBody
     * @returns any Successful Response
     * @throws ApiError
     */
    public saveEntityOidcProvider(
        entityId: string,
        requestBody: Record<string, any>,
    ): CancelablePromise<Record<string, any>> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/auth/save-entity-oidc-provider',
            query: {
                'entity_id': entityId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Test Entity Oidc Provider
     * Test entity OIDC provider configuration (requires entity admin permission)
     * @param entityId
     * @returns any Successful Response
     * @throws ApiError
     */
    public testEntityOidcProvider(
        entityId: string,
    ): CancelablePromise<Record<string, any>> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/auth/test-entity-oidc-provider',
            query: {
                'entity_id': entityId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Generate Totp Secret
     * Generate TOTP secret during MFA setup flow.
     * Returns QR code and backup codes.
     *
     * **Important**: User must save backup codes - they won't be shown again!
     *
     * This endpoint accepts an MFA token (not a full JWT) for use during login flow.
     * @param requestBody
     * @returns TOTPSetupResponse Successful Response
     * @throws ApiError
     */
    public generateTotpSecret(
        requestBody: TOTPSetupPayload,
    ): CancelablePromise<TOTPSetupResponse> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/auth/generate-totp-secret',
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Enable Totp
     * Enable TOTP by verifying first code from authenticator during MFA setup flow.
     * This activates TOTP for the user and returns a full auth token.
     *
     * Rate limited to 5 attempts - after that user must generate new secret.
     * This endpoint accepts an MFA token (not a full JWT) for use during login flow.
     * @param requestBody
     * @returns Token Successful Response
     * @throws ApiError
     */
    public enableTotp(
        requestBody: TOTPEnablePayload,
    ): CancelablePromise<Token> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/auth/enable-totp',
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Disable Totp
     * Disable TOTP for authenticated user.
     * Requires re-authentication via password or magic link.
     * @returns any Successful Response
     * @throws ApiError
     */
    public disableTotp(): CancelablePromise<Record<string, any>> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/auth/disable-totp',
        });
    }

    /**
     * Get Totp Status
     * Get TOTP status for authenticated user.
     * @returns any Successful Response
     * @throws ApiError
     */
    public getTotpStatus(): CancelablePromise<Record<string, any>> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/auth/get-totp-status',
        });
    }

    /**
     * Authenticated Generate Totp Secret
     * Generate TOTP secret for authenticated user who wants to enable TOTP.
     * Returns QR code and backup codes.
     *
     * **Important**: User must save backup codes - they won't be shown again!
     * @returns TOTPSetupResponse Successful Response
     * @throws ApiError
     */
    public authenticatedGenerateTotpSecret(): CancelablePromise<TOTPSetupResponse> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/auth/authenticated/generate-totp-secret',
        });
    }

    /**
     * Authenticated Enable Totp
     * Enable TOTP by verifying first code from authenticator for authenticated user.
     * This activates TOTP for the user.
     *
     * Rate limited to 5 attempts - after that user must generate new secret.
     * @param code
     * @returns any Successful Response
     * @throws ApiError
     */
    public authenticatedEnableTotp(
        code: string,
    ): CancelablePromise<Record<string, any>> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/auth/authenticated/enable-totp',
            query: {
                'code': code,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Setup Sms Mfa
     * Setup SMS MFA during MFA setup flow.
     * Sends verification code to provided phone number.
     *
     * This endpoint accepts an MFA token (not a full JWT) for use during login flow.
     * @param requestBody
     * @returns SMSSetupResponse Successful Response
     * @throws ApiError
     */
    public setupSmsMfa(
        requestBody: SMSSetupPayload,
    ): CancelablePromise<SMSSetupResponse> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/auth/setup-sms-mfa',
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Enable Sms Mfa
     * Enable SMS MFA by verifying code sent via SMS during MFA setup flow.
     * This activates SMS MFA for the user and returns a full auth token.
     *
     * Rate limited to 5 attempts - after that user must start setup again.
     * This endpoint accepts an MFA token (not a full JWT) for use during login flow.
     * @param requestBody
     * @returns Token Successful Response
     * @throws ApiError
     */
    public enableSmsMfa(
        requestBody: SMSEnablePayload,
    ): CancelablePromise<Token> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/auth/enable-sms-mfa',
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Send Sms Code
     * Send SMS verification code during login flow.
     * This endpoint accepts an MFA token (not a full JWT).
     * @param requestBody
     * @returns any Successful Response
     * @throws ApiError
     */
    public sendSmsCode(
        requestBody: SMSCodePayload,
    ): CancelablePromise<Record<string, any>> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/auth/send-sms-code',
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Disable Sms Mfa
     * Disable SMS MFA for authenticated user.
     * Requires re-authentication via password or magic link.
     * @returns any Successful Response
     * @throws ApiError
     */
    public disableSmsMfa(): CancelablePromise<Record<string, any>> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/auth/disable-sms-mfa',
        });
    }

    /**
     * Get Sms Status
     * Get SMS MFA status for authenticated user.
     * @returns any Successful Response
     * @throws ApiError
     */
    public getSmsStatus(): CancelablePromise<Record<string, any>> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/auth/get-sms-status',
        });
    }

    /**
     * Authenticated Setup Sms Mfa
     * Setup SMS MFA for authenticated user who wants to enable SMS.
     * Sends verification code to provided phone number.
     * @param phoneNumber
     * @returns SMSSetupResponse Successful Response
     * @throws ApiError
     */
    public authenticatedSetupSmsMfa(
        phoneNumber: string,
    ): CancelablePromise<SMSSetupResponse> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/auth/authenticated/setup-sms-mfa',
            query: {
                'phone_number': phoneNumber,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Authenticated Enable Sms Mfa
     * Enable SMS MFA by verifying code sent via SMS for authenticated user.
     * This activates SMS MFA for the user.
     *
     * Rate limited to 5 attempts - after that user must start setup again.
     * @param code
     * @returns any Successful Response
     * @throws ApiError
     */
    public authenticatedEnableSmsMfa(
        code: string,
    ): CancelablePromise<Record<string, any>> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/auth/authenticated/enable-sms-mfa',
            query: {
                'code': code,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

}
